import { prepareToast } from './toast';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/vue';
import { LocalStorageUtils, routeConstants } from '@/utils';
import router from '@/router';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { useStudyStore } from '@/stores/study';

export class LoadingStateModel {
  isLoading: boolean = false;
  isFinished: boolean = false;
  isFailed: boolean = false;
  message: string = '';
  options: object = {};

  appStore = useAppStore();
  authStore = useAuthStore();
  studyStore = useStudyStore();

  constructor({
    isLoading = false, isFinished = false, isFailed = false, msg = '', options = {}
  }) {
    this.isLoading = isLoading;
    this.isFinished = isFinished;
    this.isFailed = isFailed;
    this.message = msg;
    this.options = options;

    if (Object.keys(options).length > 0) {
      this.setOptions(options);
    }
  }
  setState({
    isLoading = false, isFinished = false, isFailed = false, msg = '', options = {}
  }) {
    this.isLoading = isLoading;
    this.isFinished = isFinished;
    this.isFailed = isFailed;
    this.message = msg;
    if (Object.keys(this.options || {}).length > 0) {
      this.setOptions(options);
    }
  }
  setOptions(options: object) {
    this.options = { ...this.options, ...options };
  }
  setError(error: unknown) {
    // console.error(error);
    this.isFailed = true;
    this.captureSentryException(error);
    let title = (error as Error).name ?? 'Something went wrong!';
    if (error instanceof AxiosError) {
      title = error.response?.data.error ?? title;
      const serverity = error.message.includes('Network') || (error.response?.status ?? 400) > 399 ? 'error' : 'success';
      if (error.response?.status == 401 && error.response?.data.message.includes('Expired')) {
        if (window.location.href.includes('/investigation/')) {
          /** This is the deep link route. Instead of logging out the user and redirecting them to the /login route,
           * we're simply clearing their expired keys and reloading the page, so it initiates the OAuth login again. */
          LocalStorageUtils.logoutUser();
          window.location.reload();
        } else {
          router.replace({
            name: routeConstants.LOGOUT.name,
            state: { message: 'Logging you out because of the expired session, please login again!' } 
          });
        }
      }
      const errorMessage = error.response?.data.message ?? error.response?.data.error ?? (error as Error).message;
      this.appStore.addToast(prepareToast(serverity, title, errorMessage));
    } else {
      this.appStore.addToast(prepareToast('error', title, (error as Error).message));
    }
  }
  setSuccess(message: string, title: string = 'Success!') {
    this.appStore.addToast(prepareToast('success', title, message));
  }
  startLoading() {
    this.isLoading = true;
  }
  stopLoadingAndFinish() {
    this.isLoading = false;
    this.isFinished = true;
  }

  captureSentryException(error: unknown) {
    const scope = Sentry.getCurrentScope();
    scope.setUser({
      id: this.authStore.userData?.identifier,
      email: this.authStore.userData?.email
    });
    scope.setExtras({
      studyId: this.studyStore.selectedStudy?.b64Identifier,
      studyName: this.studyStore.selectedStudy?.title
    });

    Sentry.captureException(error);

    if (error instanceof AxiosError) {
      const errorMessage = error.response?.data.message ?? error.response?.data.error ?? (error as Error).message;
      Sentry.captureMessage(`AxiosError: ${errorMessage}`, 'error');
    } else {
      const errorMessage = (error as Error).message;
      Sentry.captureMessage(`ErrorMessage: ${errorMessage}`, 'error');
    }
  }
}
